import { FC, ReactElement, useCallback } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";

import {
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import {
  USER_DATA_FORMAT_DECIMAL_SEPARATOR,
  USER_DATA_FORMAT_MAX_DIGITS,
  USER_DATA_FORMAT_MIN_DIGITS,
  USER_DATA_FORMAT_MODE,
  USER_DATA_FORMAT_PROFILE_UNITS,
  cloneDefaultUserDataFormatSettings,
} from "@framework/types";

import { IProfileFormValues } from "../types";

const tableCellSx = {
  borderBottom: "1px solid #ebebf7",
  py: 1,
};

export const DataFormatSettings: FC<{ hideTitle?: boolean }> = ({ hideTitle = false }): ReactElement => {
  const { control, setValue } = useFormContext<IProfileFormValues>();
  const { formatMessage } = useIntl();

  const handleResetDefaults = useCallback(() => {
    setValue("dataFormat", cloneDefaultUserDataFormatSettings(), {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
  }, [setValue]);

  return (
    <Box sx={{ mt: hideTitle ? 0 : 3 }}>
      {!hideTitle ? (
        <Typography variant="h6" gutterBottom>
          {formatMessage({
            id: "pages.profile.dataFormatTitle",
            defaultMessage: "Data format",
          })}
        </Typography>
      ) : null}

      <Box
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 1 }}
      >
        <Typography variant="subtitle1">
          {formatMessage({
            id: "pages.profile.dataFormatReportsTitle",
            defaultMessage: "Report output format",
          })}
        </Typography>
        <Button type="button" variant="outlined" size="small" onClick={handleResetDefaults}>
          {formatMessage({
            id: "pages.profile.restoreDataFormatDefaults",
            defaultMessage: "Restore default values",
          })}
        </Button>
      </Box>

      <Controller
        name="dataFormat.decimalSeparator"
        control={control}
        render={({ field }) => (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
              {formatMessage({
                id: "pages.profile.dataFormatDecimalSeparator",
                defaultMessage: "Decimal separator",
              })}
            </Typography>
            <RadioGroup row value={field.value} onChange={field.onChange}>
              <FormControlLabel
                value={USER_DATA_FORMAT_DECIMAL_SEPARATOR.DOT}
                control={<Radio size="small" />}
                label={formatMessage({
                  id: "pages.profile.dataFormatDecimalSeparatorDot",
                  defaultMessage: "Dot (.)",
                })}
              />
              <FormControlLabel
                value={USER_DATA_FORMAT_DECIMAL_SEPARATOR.COMMA}
                control={<Radio size="small" />}
                label={formatMessage({
                  id: "pages.profile.dataFormatDecimalSeparatorComma",
                  defaultMessage: "Comma (,)",
                })}
              />
            </RadioGroup>
          </Box>
        )}
      />

      <Paper
        variant="outlined"
        sx={{ overflow: "hidden", borderColor: "#ebebf7", width: "fit-content", maxWidth: "100%" }}
      >
        <Table size="small" aria-label="data-format-settings">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...tableCellSx, fontWeight: 700, width: "45%" }}>
                {formatMessage({
                  id: "pages.profile.dataFormatUnits",
                  defaultMessage: "Measurement unit",
                })}
              </TableCell>
              <TableCell sx={{ ...tableCellSx, fontWeight: 700, width: "25%" }}>
                {formatMessage({
                  id: "pages.profile.dataFormatMode",
                  defaultMessage: "Type",
                })}
              </TableCell>
              <TableCell sx={{ ...tableCellSx, fontWeight: 700, width: "30%" }}>
                {formatMessage({
                  id: "pages.profile.dataFormatDigits",
                  defaultMessage: "Digits",
                })}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {USER_DATA_FORMAT_PROFILE_UNITS.map(({ code, labelIntlId, defaultLabel }) => (
              <TableRow key={code}>
                <TableCell sx={tableCellSx}>
                  {formatMessage({
                    id: labelIntlId,
                    defaultMessage: defaultLabel,
                  })}
                </TableCell>
                <TableCell sx={tableCellSx}>
                  <Controller
                    name={`dataFormat.units.${code}.mode`}
                    control={control}
                    render={({ field }) => (
                      <TextField select fullWidth size="small" value={field.value} onChange={field.onChange}>
                        <MenuItem value={USER_DATA_FORMAT_MODE.FIXED}>
                          {formatMessage({
                            id: "pages.profile.dataFormatModeFixed",
                            defaultMessage: "Fixed",
                          })}
                        </MenuItem>
                        <MenuItem value={USER_DATA_FORMAT_MODE.FLOATING}>
                          {formatMessage({
                            id: "pages.profile.dataFormatModeFloating",
                            defaultMessage: "Floating",
                          })}
                        </MenuItem>
                      </TextField>
                    )}
                  />
                </TableCell>
                <TableCell sx={tableCellSx}>
                  <Controller
                    name={`dataFormat.units.${code}.digits`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={field.value}
                        onChange={event => field.onChange(Number(event.target.value))}
                        inputProps={{
                          min: USER_DATA_FORMAT_MIN_DIGITS,
                          max: USER_DATA_FORMAT_MAX_DIGITS,
                          step: 1,
                        }}
                      />
                    )}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};
